import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { motion, useInView } from "motion/react";
import { useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { analytics } from "@/lib/analytics";

const plans = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Get started with sample setups",
    features: ["Weekly sample setups (limited)", "Browse all setups", "Driver profiles"],
    cta: "Get Started",
    href: "/auth/signin",
    variant: "outline" as const,
    highlight: false,
    badge: null,
  },
  {
    name: "Plus",
    price: "$8",
    period: "/mo",
    description: "Full setups for your series",
    features: [
      "All setups for your chosen series",
      "Weekly updates every race week",
      "Setup notes & track conditions",
      "Choose your series",
    ],
    cta: "Start Plus",
    href: "/app/settings",
    variant: "default" as const,
    highlight: false,
    badge: "Most Popular",
  },
  {
    name: "Premium",
    price: "$15",
    period: "/mo",
    description: "The complete competitive edge",
    features: [
      "ALL setups, every series",
      "Replay files (.rpy)",
      "Telemetry data (.ibt)",
      "Priority updates",
      "Everything in Plus",
    ],
    cta: "Go Premium",
    href: "/app/settings",
    variant: "default" as const,
    highlight: true,
    badge: "Best Value",
  },
];

export function PricingSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      analytics.pricingPageViewed({ isAuthenticated: false });
    }
  }, [isInView]);

  return (
    <section id="pricing" className="relative py-24 px-4 scroll-mt-20" ref={ref}>
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <h2 className="font-display text-4xl font-bold uppercase tracking-tight text-foreground md:text-5xl">
            Choose Your <span className="text-primary">Plan</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            From free samples to the full pro package. Pick the plan that matches your ambition.
          </p>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-3 md:items-stretch">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
              className="relative"
            >
              {plan.badge && (
                <Badge
                  className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 ${
                    plan.highlight
                      ? "bg-folk-green text-white hover:bg-folk-green/90"
                      : "bg-folk-blue text-white hover:bg-folk-blue/90"
                  }`}
                >
                  {plan.badge}
                </Badge>
              )}

              <div
                className={`relative flex h-full flex-col rounded-xl border p-8 backdrop-blur-sm transition-all duration-300 overflow-hidden ${
                  plan.highlight
                    ? "border-folk-blue/50 bg-card shadow-lg shadow-folk-blue/5"
                    : "border-border bg-card/50 hover:border-primary/30"
                }`}
              >
                {/* Real Kalocsai corner decoration on Premium card */}
                {plan.highlight && (
                  <img
                    src="/kalocsai-corner-tr.webp"
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="pointer-events-none absolute -top-4 -right-4 w-44 h-44 object-contain"
                    style={{
                      opacity: 0.12,
                      maskImage:
                        "radial-gradient(ellipse at top right, black 30%, transparent 70%)",
                      WebkitMaskImage:
                        "radial-gradient(ellipse at top right, black 30%, transparent 70%)",
                    }}
                  />
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span
                      className="text-5xl font-bold text-foreground"
                      style={{
                        fontFamily: "'JetBrains Mono Variable', monospace",
                      }}
                    >
                      {plan.price}
                    </span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{plan.description}</p>
                </div>

                <ul className="mb-8 flex-1 space-y-3">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-3 text-sm text-muted-foreground"
                    >
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-folk-blue" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  asChild
                  variant={plan.variant}
                  className={`w-full ${
                    plan.highlight ? "bg-folk-blue text-white hover:bg-folk-blue/90" : ""
                  }`}
                  size="lg"
                >
                  <Link
                    to={plan.href}
                    onClick={() =>
                      analytics.landingCtaClicked({
                        location: `pricing_${plan.name.toLowerCase()}`,
                      })
                    }
                  >
                    {plan.cta}
                  </Link>
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
